// pages/chart/modular/panelDefaults.ts — discriminated-union default configs (design §6.1, m3 split)
//
// Extracted from panelRegistry.ts so the registry stays a thin component+size+flags
// table under the 250-LOC cap. Each entry is the seed `config` for a freshly-minted
// panel of that type; the registry pairs it with sizes/flags + the wrapper component.
//
// honors exactOptionalPropertyTypes: optional config fields are OMITTED here, never
// assigned `undefined`. The '__ACTIVE__' sentinel is the persisted "follow the active
// symbol" value (resolved at render time by useResolvedSymbol, §9.3) — never a code.

import type { PanelConfigByType } from './dashboardTypes';

/** Sentinel resolved to the page's active symbol at render time (§5.5/§9.3). */
const ACTIVE = '__ACTIVE__';

/**
 * Default seed config per panel type. Used by dashboardStore.addPanel/setPanelType
 * (via defaultsFor) to initialize a new panel. Indexed by PanelType so the registry
 * can spread it into PanelDefinition.defaultConfig with the correct union member.
 */
export const PANEL_DEFAULT_CONFIG: {
  [K in keyof PanelConfigByType]: PanelConfigByType[K];
} = {
  'price-chart': {
    symbol: ACTIVE,
    interval: 15,
    indicators: { emas: [9, 21], vwap: false },
  },
  'indicator-pane': {
    symbol: ACTIVE,
    interval: 15,
    indicator: 'rsi',
    params: { period: 14 },
  },
  'order-book': { symbol: ACTIVE },
  'depth-chart': { symbol: ACTIVE },
  'market-trades': { symbol: ACTIVE },
  'watchlist-panel': {},
  'trade-ticket': { symbol: ACTIVE, marketType: 'spot' },
  'ticker-stats': { symbol: ACTIVE, variant: 'full' },
  'market-overview-heatmap': { filterQuote: 'all', sortBy: 'change' },
  'forecast-panel': { symbol: ACTIVE, interval: 15 },
  'sentiment-panel': { symbol: ACTIVE },
  'positions-pl': {},
  'news-feed': { symbol: ACTIVE },
  // `raw` is optional → omit it (exactOptionalPropertyTypes).
  unknown: {},
};
